// Commitment Tracker page — FE-041 / FE-042 / Polish chunk.
//
// Header layout matches the Pencil ``BeneCRM Tasks`` frame:
//
//   [ Title + subtitle ]                          [ + New Commitment ]
//   [ Filter dropdown ] [ Search …………… ] [ Sort: ] [ List | Board ]
//   [ Filtered by: chip · chip · chip ]
//
// The legacy FE-041 ``Saved filters`` row was folded into the
// filter dropdown; ``Save current`` only renders when filters are
// active and stays disabled until the saved-filter REST endpoint
// lands.

import { Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link, useLocation, useNavigate, useSearchParams,
} from "react-router-dom";

import { useContext } from "react";

import { AuthContext } from "@/features/auth/AuthContext";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { CancellationReasonModal } from "./CancellationReasonModal";
import { CommitmentFiltersBar } from "./CommitmentFiltersBar";
import { CommitmentKanbanBoard } from "./CommitmentKanbanBoard";
import { CommitmentListTable } from "./CommitmentListTable";
import { useOptionalCommitmentModal } from "./CommitmentModalProvider";
import { computeVisibleStatuses } from "./computeVisibleStatuses";
import { FilterPopover } from "./filterBuilder/FilterPopover";
import { ToastContainer } from "./filterBuilder/Toast";
import { useToast } from "./filterBuilder/useToast";
import {
  useAssignCommitmentMutation,
  useCommitmentListQuery,
  useKanbanTransitionMutation,
  useMembersPickerQuery,
  useUpdateCommitmentMutation,
} from "./hooks";
import { describeTransitionError } from "./transitionErrorCopy";
import {
  DEFAULT_COMMITMENT_LIMIT,
  parseCommitmentCollapsedFromSearch,
  parseCommitmentFiltersFromSearch,
  parseCommitmentOffsetFromSearch,
  parseCommitmentSortFromSearch,
  parseCommitmentViewFromSearch,
  parseFilterTreeFromSearch,
  serializeCommitmentTrackerUrl,
  type CommitmentView,
} from "./urlState";
import type {
  CommitmentListFilters,
  CommitmentListRow,
  CommitmentSortKey,
  CommitmentStatus,
} from "./types";


// Both views run per-bucket infinite-scroll fetches (list groups +
// board columns); the page-level query is only needed to source
// ``status_counts``, so we request a single row to keep the
// response small.
const COUNTS_FETCH_LIMIT = 1;


// User-facing labels for the six sort keys BE-052 accepts. Anything
// outside this allow-list is rejected by the backend, so we only
// surface keys the API can actually honour.
const SORT_LABELS: Record<CommitmentSortKey, string> = {
  due_date_time: "Due date",
  title: "Title",
  status: "Status",
  priority: "Priority",
  updated_at: "Updated",
  commitment_number: "Commitment #",
};


export function CommitmentTrackerPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();

  const filters = useMemo<CommitmentListFilters>(
    () => parseCommitmentFiltersFromSearch(search),
    [search],
  );
  const { sortBy, sortDir } = useMemo(
    () => parseCommitmentSortFromSearch(search),
    [search],
  );
  const offset = useMemo(
    () => parseCommitmentOffsetFromSearch(search),
    [search],
  );
  const view = useMemo(
    () => parseCommitmentViewFromSearch(search),
    [search],
  );
  const filterTree = useMemo(
    () => parseFilterTreeFromSearch(search),
    [search],
  );
  const collapsedStatuses = useMemo(
    () => parseCommitmentCollapsedFromSearch(search),
    [search],
  );
  const hasActiveFilters = useMemo(
    () => Object.values(filters).some(
      (v) => v !== undefined && v !== null && v !== "",
    ),
    [filters],
  );

  const updateUrl = useCallback(
    (
      nextFilters: CommitmentListFilters,
      nextSortBy: CommitmentSortKey = sortBy,
      nextSortDir: "asc" | "desc" = sortDir,
      nextOffset: number = 0,
      nextView: CommitmentView = view,
    ) => {
      const url = serializeCommitmentTrackerUrl(
        nextFilters, nextSortBy, nextSortDir, nextOffset, nextView,
        filterTree,
        collapsedStatuses,
      );
      navigate(url, { replace: false });
    },
    [navigate, sortBy, sortDir, view, filterTree, collapsedStatuses],
  );

  const setCollapsedStatuses = useCallback(
    (next: CommitmentStatus[]) => {
      const url = serializeCommitmentTrackerUrl(
        filters, sortBy, sortDir, offset, view, filterTree, next,
      );
      navigate(url, { replace: true });
    },
    [navigate, filters, sortBy, sortDir, offset, view, filterTree],
  );

  const setView = useCallback(
    (nextView: CommitmentView) =>
      updateUrl(filters, sortBy, sortDir, 0, nextView),
    [filters, sortBy, sortDir, updateUrl],
  );

  // Both views delegate row fetching to per-bucket infinite queries
  // (list groups + board columns); the page-level query exists only
  // to surface ``status_counts`` for the count badges, so we ask
  // for a single row to keep the payload tiny.
  const query = useCommitmentListQuery({
    filters,
    filterTree,
    sortBy, sortDir,
    limit: COUNTS_FETCH_LIMIT,
    offset: 0,
  });

  const toast = useToast();
  // Read the auth context directly so this page can mount in
  // tests without spinning up a full AuthProvider. Falls back to
  // ``null`` outside the provider; the Quick Filter "Assigned to
  // me" path no-ops if we don't know who the user is.
  const auth = useContext(AuthContext);

  function applyFilterTree(nextTree: import("./urlState").FilterTree | undefined): void {
    const url = serializeCommitmentTrackerUrl(
      filters, sortBy, sortDir, 0, view, nextTree,
    );
    navigate(url, { replace: false });
  }

  // ── Quick Filters (FE-043b) ─────────────────────────────────────
  // Each preset rewrites the URL with the desired flat filters and
  // clears the advanced filter tree so the two systems don't fight.
  function applyQuickFilter(
    key: import("./filterBuilder/FilterPopover").QuickFilterKey,
  ): void {
    let next: typeof filters;
    if (key === "all_work") {
      next = {};
    } else if (key === "assigned_to_me") {
      next = auth?.user?.id
        ? { ...filters, assigned_to: auth.user.id, overdue: undefined }
        : filters;
    } else if (key === "overdue") {
      next = { ...filters, overdue: true };
    } else if (key === "private") {
      // Private = visibility filter pass-through. Drop the archive
      // flag so the user lands on active private work, not archived.
      next = { ...filters, visibility: "private", archived: undefined };
    } else if (key === "archive") {
      // Archive flips the tracker into the archived view. Cleared
      // visibility so we don't accidentally combine private+archive.
      next = { ...filters, archived: true, visibility: undefined };
    } else {
      next = filters;
    }
    const url = serializeCommitmentTrackerUrl(
      next, sortBy, sortDir, 0, view, undefined,
    );
    navigate(url, { replace: false });
  }

  // ── Kanban movement (FE-042b) ───────────────────────────────────
  // The board calls ``onMoveCard`` for every drop / menu pick. We
  // fire the transition mutation directly; it handles optimistic
  // patching of the list cache + rollback on failure. The Cancelled
  // target needs a reason, so we stage that move into a modal and
  // only fire the mutation once the user confirms.
  const moveMutation = useKanbanTransitionMutation();
  const [pendingCancel, setPendingCancel] = useState<
    { commitment_id: string; title: string } | null
  >(null);

  const handleMoveCard = useCallback(
    (commitmentId: string, toStatus: CommitmentStatus) => {
      if (toStatus === "cancelled") {
        const target: CommitmentListRow | undefined =
          query.data?.rows.find((r) => r.commitment_id === commitmentId);
        setPendingCancel({
          commitment_id: commitmentId,
          title: target?.title ?? "this Commitment",
        });
        return;
      }
      moveMutation.mutate(
        { commitment_id: commitmentId, to_status: toStatus },
        {
          onError: (err) => {
            toast.push(
              describeTransitionError(err)
                ?? "Couldn't update the Commitment status.",
              "error",
            );
          },
        },
      );
    },
    [moveMutation, query.data, toast],
  );

  function confirmCancellation(reason: string): void {
    if (!pendingCancel) return;
    const id = pendingCancel.commitment_id;
    moveMutation.mutate(
      {
        commitment_id: id,
        to_status: "cancelled",
        cancellation_reason: reason,
      },
      {
        onSuccess: () => {
          setPendingCancel(null);
        },
        onError: (err) => {
          // Keep the modal open so the user can read the error and
          // retry without retyping their reason.
          toast.push(
            describeTransitionError(err)
              ?? "Couldn't cancel the Commitment.",
            "error",
          );
        },
      },
    );
  }

  // ── List inline assignee + due date (FE-043b parity v3) ─────────
  const assignMutation = useAssignCommitmentMutation();
  const updateRowMutation = useUpdateCommitmentMutation();
  const membersQuery = useMembersPickerQuery();
  const handleAssignRow = useCallback(
    (commitmentId: string, userId: string | null) => {
      assignMutation.mutate(
        {
          commitment_id: commitmentId,
          input: {
            assigned_to: userId,
            assigned_job_title: null,
            assigned_client_contact_id: null,
          },
        },
        {
          onError: (err) => {
            toast.push(
              describeTransitionError(err)
                ?? "Couldn't update assignee.",
              "error",
            );
          },
        },
      );
    },
    [assignMutation, toast],
  );

  const handleSetDueDate = useCallback(
    (commitmentId: string, isoOrNull: string | null) => {
      updateRowMutation.mutate(
        {
          commitment_id: commitmentId,
          values: { due_date_time: isoOrNull },
        },
        {
          onError: (err) => {
            toast.push(
              describeTransitionError(err)
                ?? "Couldn't update due date.",
              "error",
            );
          },
        },
      );
    },
    [updateRowMutation, toast],
  );

  const pendingCommitmentId = moveMutation.isPending
    ? moveMutation.variables?.commitment_id ?? null
    : assignMutation.isPending
      ? assignMutation.variables?.commitment_id ?? null
      : null;

  // FE-043c: hook into the global Commitment modal provider so the
  // ``+ New Commitment`` CTA opens the quick-create modal over the
  // current tracker URL, preserving filters / search / view. The
  // ``useOptional`` variant gracefully falls back to the legacy
  // ``/dashboard/commitments/new`` link when the page is rendered
  // in tests without the provider.
  const commitmentModal = useOptionalCommitmentModal();

  // Stamp every detail Link with a ``state`` carrying the current
  // filtered URL so:
  //   1. The detail-modal route overlay can render the modal on
  //      top of this filtered page (Commitments-Modal-01).
  //   2. The full-page detail Back link can return here when the
  //      user lands on the detail route directly (refresh or deep
  //      link). Direct loads have no ``backgroundLocation`` set,
  //      so the modal route falls through to the page surface.
  const location = useLocation();
  const buildLinkState = useCallback(
    () => ({
      backgroundLocation: location,
      fromUrl: `${location.pathname}${location.search}`,
      fromLabel: view === "board"
        ? "Back to Commitment Tracker (Board)"
        : "Back to Commitment Tracker",
    }),
    [location, view],
  );

  return (
    <div
      data-testid="commitment-tracker-page"
      // List view defers scrolling to the layout's ``<main>`` so we
      // don't stack two scroll containers — programmatic scroll
      // restorations on navigation can otherwise leave main scrolled
      // past the page's sticky toolbar with no way back ("top is gone +
      // can't scroll up").
      //
      // Board view needs a bounded height so each column can scroll
      // its card list independently: without ``h-full min-h-0`` here,
      // the board's ``flex-1 min-h-0`` has no height to flex into,
      // each column's ``h-full`` resolves to natural content height,
      // and the column ``<ul>``'s ``overflow-y-auto`` never engages —
      // so main scrolls the whole board instead of each lane scrolling
      // on its own. The board view doesn't introduce a second scroll
      // container (the page-div has no ``overflow``); only the column
      // ULs scroll. The page-div height is just a height bound for the
      // flex chain.
      className={[
        "flex min-w-0 flex-col gap-4",
        view === "board" ? "h-full min-h-0" : "",
      ].join(" ")}
    >
      {/* Sticky page toolbar — keeps the Commitments title + Filter +
          Search + Sort + view toggle + "+ New Commitment" pinned at
          the top of the scrolling content as the user scrolls
          through long status groups. ``min-h-12`` (3rem ≈ 48px) is
          chosen so the toolbar's bottom edge lands exactly where
          the group section headers stick (``top-12``) — without it
          the toolbar can render shorter than 48px and a hairline
          of scroll-through content peeks between the two.

          The ``before:`` pseudo-element extends an opaque ``bg-
          canvas`` strip 24px above the toolbar so scrolling rows
          can't bleed through <main>'s ``py-6`` padding-top region
          when the toolbar is stuck at ``top-0``. It's the cleanest
          way to plug that gap without doing arithmetic on the
          toolbar's own height (which would have to track exactly
          where the per-status sticky headers anchor at ``top-12``).
          ``pointer-events-none`` keeps it from intercepting clicks
          headed for the navigation chrome above. */}
      <header
        className="sticky top-0 z-30 flex min-h-12 flex-col justify-end gap-2 bg-canvas pb-2 before:pointer-events-none before:absolute before:bottom-full before:left-0 before:right-0 before:h-6 before:bg-canvas before:content-['']"
      >
        {/* Single-row top control: title sits inline with the filter
            dropdown on the left; search stretches the middle; sort,
            view toggle, and the "+ New Commitment" CTA cluster on
            the right. Matches the Pencil ``BeneCRM Tasks`` frame
            (no subtitle, no two-line split). */}
        <div
          data-testid="commitment-tracker-controls"
          className="flex flex-wrap items-center gap-2"
        >
          <h1 className="text-lg font-semibold tracking-tight text-text-primary">
            Commitments
          </h1>
          <FilterPopover
            appliedTree={filterTree}
            onApply={applyFilterTree}
            onQuickFilter={applyQuickFilter}
            pushToast={toast.push}
          />
          <SearchInput
            value={filters.search ?? ""}
            onSubmit={(next) =>
              updateUrl(
                { ...filters, search: next || undefined },
                sortBy, sortDir, 0,
              )
            }
          />
          <SortDropdown
            sortBy={sortBy}
            onChange={(nextSortBy) =>
              updateUrl(filters, nextSortBy, sortDir, 0)
            }
          />
          <ViewToggle view={view} onChange={setView} />
          {commitmentModal ? (
            <button
              type="button"
              data-testid="commitment-tracker-new"
              onClick={() => {
                commitmentModal.openCreateCommitmentModal({
                  defaults: filters.client_id
                    ? { account_id: filters.client_id }
                    : undefined,
                  returnContext: {
                    fromUrl: `${location.pathname}${location.search}`,
                    fromLabel: "Back to Commitment Tracker",
                  },
                });
              }}
              className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
            >
              + New Commitment
            </button>
          ) : (
            <Link
              to="/dashboard/commitments/new"
              data-testid="commitment-tracker-new"
              className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
            >
              + New Commitment
            </Link>
          )}
        </div>

        <CommitmentFiltersBar
          filters={filters}
          onChange={(next) => updateUrl(next, sortBy, sortDir, 0)}
        />
      </header>
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />

      {query.isLoading ? (
        <DashboardLoadingState
          variant={view === "board" ? "card" : "table"}
          rows={6}
        />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load Commitments"
          description="Refresh to retry, or check the network connection."
          retry={() => query.refetch()}
        />
      ) : query.data && (query.data.total > 0) ? (
        view === "board" ? (
          <CommitmentKanbanBoard
            rows={query.data.rows}
            statusCounts={query.data.status_counts}
            // Per-column infinite scroll — each board column self-
            // fetches its bucket and lazy-loads more cards on scroll
            // so the board doesn't run out mid-scroll on long
            // trackers.
            bucketFetch={{ filters, filterTree, sortBy, sortDir }}
            visibleStatuses={computeVisibleStatuses(filters, filterTree)}
            onMoveCard={handleMoveCard}
            onAssignCard={handleAssignRow}
            onSetCardDueDate={handleSetDueDate}
            members={membersQuery.data?.members ?? []}
            pendingCommitmentId={pendingCommitmentId}
            buildLinkState={buildLinkState}
          />
        ) : (
          <CommitmentListTable
            rows={query.data.rows}
            total={query.data.total}
            statusCounts={query.data.status_counts}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={({ sortBy: nextBy, sortDir: nextDir }) =>
              updateUrl(filters, nextBy, nextDir, 0)
            }
            limit={DEFAULT_COMMITMENT_LIMIT}
            offset={offset}
            onOffsetChange={(next) =>
              updateUrl(filters, sortBy, sortDir, next)
            }
            groupByStatus
            // List view delegates row fetching to per-bucket infinite
            // queries inside the table; the page only sources counts.
            groupedFetch={{ filters, filterTree, sortBy, sortDir }}
            collapsedStatuses={collapsedStatuses}
            onCollapseChange={setCollapsedStatuses}
            visibleStatuses={computeVisibleStatuses(filters, filterTree)}
            onMoveStatus={handleMoveCard}
            onAssign={handleAssignRow}
            onSetDueDate={handleSetDueDate}
            members={membersQuery.data?.members ?? []}
            pendingCommitmentId={pendingCommitmentId}
            buildLinkState={buildLinkState}
          />
        )
      ) : (
        <EmptyState
          hasActiveFilters={hasActiveFilters}
          onClear={() => updateUrl({}, sortBy, sortDir, 0)}
        />
      )}

      <CancellationReasonModal
        open={pendingCancel !== null}
        title={pendingCancel?.title ?? ""}
        isPending={moveMutation.isPending}
        onCancel={() => setPendingCancel(null)}
        onConfirm={confirmCancellation}
      />
    </div>
  );
}


// ── Search input ──────────────────────────────────────────────────────────


function SearchInput({
  value, onSubmit,
}: {
  value: string;
  onSubmit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);
  // Debounced auto-apply: typing in the search box updates the
  // URL after a short pause, no Enter required. Fixes the
  // "kanban search isn't working" report — the form-submit path
  // never fired because users expected ClickUp / Linear-style
  // search-as-you-type.
  const lastFiredRef = useRef(value);
  useEffect(() => {
    const next = draft.trim();
    if (next === lastFiredRef.current) return;
    const t = window.setTimeout(() => {
      lastFiredRef.current = next;
      onSubmit(next);
    }, 250);
    return () => window.clearTimeout(t);
  }, [draft, onSubmit]);
  return (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(draft.trim());
      }}
      className="flex-1 sm:max-w-xs"
    >
      <label className="relative flex items-center">
        <span className="sr-only">Search Commitments</span>
        <Search
          size={14}
          aria-hidden="true"
          className="pointer-events-none absolute left-2.5 text-text-tertiary"
        />
        <input
          type="search"
          data-testid="commitments-search-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Search Commitments"
          className="w-full rounded-md border border-border-subtle bg-raised py-1.5 pl-7 pr-3 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
        />
      </label>
    </form>
  );
}


// ── Sort dropdown ─────────────────────────────────────────────────────────


function SortDropdown({
  sortBy, onChange,
}: {
  sortBy: CommitmentSortKey;
  onChange: (next: CommitmentSortKey) => void;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-raised px-2.5 py-1 text-xs font-medium text-text-primary">
      <span className="text-text-tertiary">Sort:</span>
      <select
        data-testid="commitments-sort-select"
        value={sortBy}
        onChange={(e) =>
          onChange(e.target.value as CommitmentSortKey)
        }
        className="cursor-pointer rounded-md bg-transparent text-xs font-medium text-text-primary focus:outline-none"
      >
        {(Object.keys(SORT_LABELS) as CommitmentSortKey[]).map((key) => (
          <option key={key} value={key}>{SORT_LABELS[key]}</option>
        ))}
      </select>
    </label>
  );
}


// ── List/Board toggle ─────────────────────────────────────────────────────


interface ViewToggleProps {
  view: CommitmentView;
  onChange: (next: CommitmentView) => void;
}


function ViewToggle({ view, onChange }: ViewToggleProps) {
  return (
    <div
      data-testid="commitment-tracker-view-toggle"
      role="group"
      aria-label="Commitment Tracker view"
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised p-1"
    >
      {/* Board first to match the Pencil frame order; ``view=list``
          remains the default URL state when no ``view=`` param is
          set, so the toggle order doesn't change the default. */}
      <ToggleButton
        active={view === "board"}
        testId="commitment-tracker-view-board"
        onClick={() => onChange("board")}
        label="Board"
      />
      <ToggleButton
        active={view === "list"}
        testId="commitment-tracker-view-list"
        onClick={() => onChange("list")}
        label="List"
      />
    </div>
  );
}


function ToggleButton({
  active, testId, onClick, label,
}: {
  active: boolean;
  testId: string;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      data-testid={testId}
      onClick={onClick}
      className={[
        "rounded px-2.5 py-1 text-xs font-medium transition-fast ease-out",
        active
          ? "bg-teal-tint text-text-primary"
          : "text-text-secondary hover:text-text-primary",
      ].join(" ")}
    >
      {label}
    </button>
  );
}


// ── Empty state ───────────────────────────────────────────────────────────


function EmptyState({
  hasActiveFilters, onClear,
}: { hasActiveFilters: boolean; onClear: () => void }) {
  if (hasActiveFilters) {
    return (
      <DashboardEmptyState
        title="No Commitments match these filters"
        description="Clear the filters to see every Commitment in your brokerage."
        action={
          <button
            type="button"
            data-testid="commitments-empty-clear"
            onClick={onClear}
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
          >
            Clear filters
          </button>
        }
      />
    );
  }
  return (
    <DashboardEmptyState
      title="No Commitments yet"
      description="Once Commitments are created — manually, by Playbook, or by BeneAI — they appear here."
    />
  );
}


